import { Routes } from '@angular/router';
import { NotFound } from './views/error-pages/not-found/not-found';
import { Unauthorized } from './views/error-pages/unauthorized/unauthorized';
import { Auth } from './views/auth/auth';
import { Base } from './views/layout/base/base';
import { CustomerDetails } from './views/customers/customer-details/customer-details';
import { authenticationGuard } from './core/guards/authentication/authentication-guard';
import { MerchantsDetails } from './views/merchants/merchants-details/merchants-details';
import { SupportsBase } from './views/supports/supports-base/supports-base';
import { titleResolver } from './core/resolvers/title-resolver/title.resolver';

export const routes: Routes = [
  {
    path: '',
    component: Base,
    canActivate: [authenticationGuard],
    children: [
      {
        path: 'dashboard',
        data: { title: 'sideMenu.dashboard' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () => import('./views/home/home').then((m) => m.Home),
      },
      {
        path: 'brands',
        data: { title: 'sideMenu.brands' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/brands/all-brands/all-brands').then(
            (m) => m.AllBrands
          ),
      },
      {
        path: 'categories',
        data: { title: 'sideMenu.categories' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/categories/all-categories/all-categories').then(
            (m) => m.AllCategories
          ),
      },
      {
        path: 'top-merchants',
        data: { title: 'sideMenu.topMerchants' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import(
            './views/top-merchants/all-top-merchants/all-top-merchants'
          ).then((m) => m.AllTopMerchants),
      },
      {
        path: 'merchants-tiers',
        data: { title: 'sideMenu.merchantTiers' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import(
            './views/merchants-tiers/all-merchants-tiers/all-merchants-tiers'
          ).then((m) => m.AllMerchantsTiers),
      },
      {
        path: 'categories',
        children: [
          {
            path: '',
            data: { title: 'sideMenu.categories' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/categories/all-categories/all-categories').then(
                (m) => m.AllCategories
              ),
          },
        ],
      },
      {
        path: 'support',
        title: 'sideMenu.support',
        component: SupportsBase,
        children: [
          {
            path: 'customers',
            data: { title: 'Customers support' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/supports/customers-support/customers-support'
              ).then((m) => m.CustomersSupport),
          },
          {
            path: 'merchants',
            data: { title: 'Merchants support' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/supports/merchants-support/merchants-support'
              ).then((m) => m.MerchantsSupport),
          },
        ],
      },
      {
        path: 'promo-codes',
        data: { title: 'sideMenu.promoCodes' },
        resolve: { pageTitle: titleResolver },
        children: [
          {
            path: '',
            title: 'Promo codes',
            loadComponent: () =>
              import(
                './views/promo-codes/all-promo-codes/all-promo-codes'
              ).then((m) => m.AllPromoCodes),
          },
          {
            path: ':promo-codeID',
            data: { title: 'Promo code details' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/promo-codes/promo-code-details/promo-code-details'
              ).then((m) => m.PromoCodeDetails),
          },
        ],
      },
      {
        path: 'finance-management',
        data: { title: 'sideMenu.financeManagement' },
        resolve: { pageTitle: titleResolver },
        children: [
          {
            path: '',
            title: 'Finance Management',
            loadComponent: () =>
              import(
                './views/finance-management/all-finance-management/all-finance-management'
              ).then((m) => m.AllFinanceManagement),
          },
          {
            path: ':finance-managementID',
            data: { title: 'Finance Management details' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/finance-management/all-transactions/all-transactions'
              ).then((m) => m.AllTransactions),
          },
        ],
      },
      {
        path: 'merchants',
        data: { title: 'sideMenu.merchants' },
        resolve: { pageTitle: titleResolver },
        children: [
          {
            path: '',
            data: { title: 'Merchants' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/merchants/all-merchants/all-merchants').then(
                (m) => m.AllMerchants
              ),
          },
          {
            path: ':merchantID/applications',
            data: { title: 'Merchants applications' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/merchants/merchants-applications-details/merchants-applications-details'
              ).then((m) => m.MerchantsApplicationsDetails),
          },
          {
            path: ':merchantID',
            data: { title: 'sideMenu.merchants' },
            resolve: { pageTitle: titleResolver },
            component: MerchantsDetails,
            children: [
              {
                path: 'overview',
                data: { title: 'Merchant overview' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/merchants/merchant-overview/merchant-overview'
                  ).then((m) => m.MerchantOverview),
              },
              {
                path: 'commission',
                data: { title: 'Merchant commission' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/merchants/merchant-commission/merchant-commission'
                  ).then((m) => m.MerchantCommission),
              },
              {
                path: 'orders',
                data: { title: 'Merchant orders' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import('./views/orders/merchant-orders/merchant-orders').then(
                    (m) => m.MerchantOrders
                  ),
              },
              {
                path: 'products',
                data: { title: 'Merchant products' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/products/merchants-products/merchants-products'
                  ).then((m) => m.MerchantsProducts),
              },
              {
                path: 'supports',
                data: { title: 'Merchant supports' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/merchants/merchant-supports/merchant-supports'
                  ).then((m) => m.MerchantSupports),
              },
            ],
          },
        ],
      },
      {
        path: 'products',
        data: { title: 'sideMenu.products' },
        resolve: { pageTitle: titleResolver },
        children: [
          {
            path: '',
            title: 'Products',
            loadComponent: () =>
              import('./views/products/all-products/all-products').then(
                (m) => m.AllProducts
              ),
          },
          {
            path: ':productID/applications',
            data: { title: 'Product application' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import(
                './views/products/products-applications-details/products-applications-details'
              ).then((m) => m.ProductsApplicationsDetails),
          },
          {
            path: ':productID/update-product',
            data: { title: 'Update Product' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/products/update-product/update-product').then(
                (m) => m.UpdateProduct
              ),
          },
          {
            path: ':productID',
            data: { title: 'sideMenu.products' },
            resolve: { pageTitle: titleResolver },
            component: MerchantsDetails,
            children: [
              {
                path: 'overview',
                data: { title: 'Merchant overview' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/merchants/merchant-overview/merchant-overview'
                  ).then((m) => m.MerchantOverview),
              },
            ],
          },
        ],
      },
      {
        path: 'collections',
        data: { title: 'sideMenu.collections' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/collections/all-collections/all-collections').then(
            (m) => m.AllCollections
          ),
      },
      {
        path: 'product-tags',
        data: { title: 'sideMenu.productTags' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/product-tags/all-product-tags/all-product-tags').then(
            (m) => m.AllProductTags
          ),
      },
      {
        path: 'sub-categories',
        data: { title: 'sideMenu.subCategories' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import(
            './views/sub-categories/all-sub-categories/all-sub-categories'
          ).then((m) => m.AllSubCategories),
      },
      {
        path: 'target-groups',
        data: { title: 'sideMenu.targetGroups' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import(
            './views/target-groups/all-target-groups/all-target-groups'
          ).then((m) => m.AllTargetGroups),
      },
      {
        path: 'countries',
        data: { title: 'sideMenu.countries' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/countries/all-countries/all-countries').then(
            (m) => m.AllCountries
          ),
      },
      {
        path: 'cities',
        data: { title: 'sideMenu.cities' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/cities/all-cities/all-cities').then(
            (m) => m.AllCities
          ),
      },
      {
        path: 'ads',
        data: { title: 'sideMenu.ads' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import('./views/ads/all-ads/all-ads').then((m) => m.AllAds),
      },
      {
        path: 'notifications',
        data: { title: 'sideMenu.notifications' },
        resolve: { pageTitle: titleResolver },
        loadComponent: () =>
          import(
            './views/notifications/all-notifications/all-notifications'
          ).then((m) => m.AllNotifications),
      },
      {
        path: 'orders',

        children: [
          {
            path: '',
            data: { title: 'sideMenu.orders' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/orders/all-orders/all-orders').then(
                (m) => m.AllOrders
              ),
          },
          {
            path: ':orderID',
            data: { title: 'orders.orderDetails' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/orders/order-details/order-details').then(
                (m) => m.OrderDetails
              ),
          },
        ],
      },
      {
        path: 'customers',

        children: [
          {
            path: '',
            data: { title: 'sideMenu.customer' },
            resolve: { pageTitle: titleResolver },
            loadComponent: () =>
              import('./views/customers/all-customers/all-customers').then(
                (m) => m.AllCustomers
              ),
          },
          {
            path: ':customerID',
            data: { title: 'customers.customerDetails' },
            resolve: { pageTitle: titleResolver },
            component: CustomerDetails,
            children: [
              {
                path: 'overview',
                data: { title: 'sideMenu.overview' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/customers/customer-overview/customer-overview'
                  ).then((m) => m.CustomerOverview),
              },
              {
                path: 'orders',
                data: { title: 'sideMenu.orders' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import('./views/orders/customer-orders/customer-orders').then(
                    (m) => m.CustomerOrders
                  ),
              },
              {
                path: 'supports',
                data: { title: 'sideMenu.supportRequests' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/supports/customer-supports/customer-supports'
                  ).then((m) => m.CustomerSupports),
              },

              {
                path: 'wallet',
                data: { title: 'sideMenu.walletHistory' },
                resolve: { pageTitle: titleResolver },
                loadComponent: () =>
                  import(
                    './views/customers/customer-wallet-history/customer-wallet-history'
                  ).then((m) => m.CustomerWalletHistory),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'auth',
    component: Auth,
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./views/auth/login/login').then((m) => m.Login),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./views/auth/forgot-password/forgot-password').then(
            (m) => m.ForgotPassword
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./views/auth/reset-password/reset-password').then(
            (m) => m.ResetPassword
          ),
      },
    ],
  },
  {
    path: 'errors',
    children: [{ path: 'unauthorized', component: Unauthorized }],
  },
  { path: '**', component: NotFound },
];
